import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { EngineToStudioMessage, ProjectManifest, StudioToEngineMessage } from "@pix/shared";

export interface TestPlayHandle {
  loadAndStart: (
    manifest: ProjectManifest,
    scripts: Record<string, string>,
    assets?: Record<string, string>,
  ) => void;
  reload: () => void;
}

interface Props {
  engineUrl: string;
  onLog?: (message: string) => void;
}

export const TestPlay = forwardRef<TestPlayHandle, Props>(function TestPlay(
  { engineUrl, onLog },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [engineReady, setEngineReady] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const pendingProject = useRef<{
    manifest: ProjectManifest;
    scripts: Record<string, string>;
    assets: Record<string, string>;
  } | null>(null);

  useImperativeHandle(ref, () => ({
    loadAndStart(manifest, scripts, assets = {}) {
      const payload = { manifest, scripts, assets };
      pendingProject.current = payload;
      if (engineReady && iframeRef.current?.contentWindow) {
        sendToEngine(iframeRef.current.contentWindow, {
          type: "loadProject",
          manifest,
          scripts,
          assets,
          assetBaseUrl: "./sample",
        });
        window.setTimeout(() => {
          if (iframeRef.current?.contentWindow) {
            sendToEngine(iframeRef.current.contentWindow, { type: "start" });
          }
        }, 50);
      }
    },
    reload() {
      setEngineReady(false);
      setIframeKey((k) => k + 1);
    },
  }));

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const msg = e.data as EngineToStudioMessage;
      if (typeof msg !== "object" || msg === null || !("type" in msg)) return;
      if (msg.type === "engineReady") {
        setEngineReady(true);
        onLog?.("[engine] ready");
        // pending があれば送る
        if (pendingProject.current && iframeRef.current?.contentWindow) {
          const p = pendingProject.current;
          sendToEngine(iframeRef.current.contentWindow, {
            type: "loadProject",
            manifest: p.manifest,
            scripts: p.scripts,
            assets: p.assets,
            assetBaseUrl: "./sample",
          });
          window.setTimeout(() => {
            if (iframeRef.current?.contentWindow) {
              sendToEngine(iframeRef.current.contentWindow, { type: "start" });
            }
          }, 50);
        }
      } else if (msg.type === "projectLoaded") {
        onLog?.("[engine] project loaded");
      } else if (msg.type === "messageShown") {
        const prefix = msg.speaker ? `${msg.speaker}: ` : "";
        onLog?.(`[engine] ${prefix}${msg.text}`);
      } else if (msg.type === "log") {
        onLog?.(`[engine ${msg.level}] ${msg.message}`);
      } else if (msg.type === "scriptEnded") {
        onLog?.("[engine] script ended");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onLog]);

  const src = `${engineUrl}?mode=embed`;

  return (
    <div className="test-play">
      <div className="test-play-bar">
        <span className="test-play-status">
          {engineReady ? "● ready" : "○ booting..."}
        </span>
      </div>
      <iframe
        key={iframeKey}
        ref={iframeRef}
        title="Pix Engine Preview"
        src={src}
        scrolling="no"
      />
    </div>
  );
});

function sendToEngine(win: Window, msg: StudioToEngineMessage): void {
  win.postMessage(msg, "*");
}
